<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { Link, page } from '@inertiajs/svelte';
    import AnswerModal from '../components/AnswerModal.svelte';
    import MediaLightbox from '../components/MediaLightbox.svelte';
    import { login } from '@/routes';
    import { store } from '@/routes/cases/answers';
    import { formatDate } from '../utils/dates';

    let { case: c, schedule, questions = [] } = $props();

    const status = $derived(c.computed_status);
    const isOpen = $derived(status === 'open');
    const isRevealed = $derived(status === 'revealed' || status === 'closed');

    const [year, month, day] = $derived(schedule.date.split('-'));
    const formattedDate = $derived(formatDate(schedule.date, { month: 'long', day: 'numeric', year: 'numeric' }));
    const shortDate = $derived(formatDate(schedule.date, { month: 'short', day: 'numeric', year: 'numeric' }));
    const answerUrl = $derived(store.url({ year: +year, month: +month, day: +day, sqid: schedule.sqid }));

    let activeQuestionId = $state<number | null>(null);

    let now = $state(new Date());
    $effect(() => {
        const interval = setInterval(() => { now = new Date(); }, 60_000);
        return () => clearInterval(interval);
    });

    const revealCountdown = $derived.by(() => {
        if (!schedule.reveal_at) return null;
        const revealAt = new Date(schedule.reveal_at);
        const diffMs = revealAt.getTime() - now.getTime();
        if (diffMs <= 0) return null;
        const totalMinutes = Math.ceil(diffMs / 60_000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h`;
        return `${minutes}m`;
    });


    const lightboxImages = $derived(
        (c.media ?? []).map((m: any) => ({ src: m.url, alt: m.caption || 'Case image', caption: m.caption || '' }))
    );
    let lightboxOpen = $state(false);
    let lightboxIndex = $state(0);
    let lightboxRef: MediaLightbox;

    let shareState: 'idle' | 'copied' = $state('idle');
    let shareTimeout: ReturnType<typeof setTimeout>;

    async function share() {
        const url = window.location.href;
        const title = `${c.topic.name} — Case of the Day`;

        if (navigator.share) {
            try {
                await navigator.share({ title, url });
            } catch {
                // user cancelled — ignore
            }
            return;
        }

        await navigator.clipboard.writeText(url);
        shareState = 'copied';
        clearTimeout(shareTimeout);
        shareTimeout = setTimeout(() => { shareState = 'idle'; }, 2000);
    }

    function openLightbox(index: number, e: MouseEvent) {
        const img = (e.currentTarget as HTMLElement).querySelector('img') as HTMLElement;
        lightboxRef.openFrom(index, img);
    }

    const additionalLightboxImages = $derived(
        (c.additional_media ?? []).map((m: any) => ({ src: m.url, alt: m.caption || 'Additional image', caption: m.caption || '' }))
    );
    let additionalLightboxOpen = $state(false);
    let additionalLightboxIndex = $state(0);
    let additionalLightboxRef: MediaLightbox;

    function openAdditionalLightbox(index: number, e: MouseEvent) {
        const img = (e.currentTarget as HTMLElement).querySelector('img') as HTMLElement;
        additionalLightboxRef.openFrom(index, img);
    }

</script>

<svelte:head>
    <title>{c.topic.name} for {shortDate} — Case of the Day</title>
</svelte:head>

<div class="max-w-3xl mx-auto space-y-8">
        <header>
            <div class="flex justify-between items-start">
                <div>
                    <p class="text-sm font-medium text-cyan-400 uppercase tracking-wide">{c.topic.name}</p>
                    <h1 class="text-2xl font-bold text-gray-100 mt-1">Case of the Day</h1>
                    <p class="text-gray-500 mt-0.5">{formattedDate}</p>
                </div>
                <button
                    type="button"
                    onclick={share}
                    class="shrink-0 mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-400 hover:text-cyan-400 hover:bg-gray-800 transition-colors"
                >
                    {#if shareState === 'copied'}
                        <svg class="size-4 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M434.8 70.1c14.3 10.4 17.5 30.4 7.1 44.7l-256 352c-5.5 7.6-14 12.3-23.4 13.1s-18.5-2.7-25.1-9.3l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l101.5 101.5 234-321.7c10.4-14.3 30.4-17.5 44.7-7.1z"/></svg>
                        <span class="text-green-400">Link copied!</span>
                    {:else}
                        <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path opacity=".4" fill="currentColor" d="M32.5 176c0-44.2 35.8-80 80-80l16 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-16 0c-8.8 0-16 7.2-16 16l0 256c0 8.8 7.2 16 16 16l256 0c8.8 0 16-7.2 16-16l0-32c0-17.7 14.3-32 32-32s32 14.3 32 32l0 32c0 44.2-35.8 80-80 80l-256 0c-44.2 0-80-35.8-80-80l0-256z"/><path fill="currentColor" d="M399.3 1.8c-9 3.7-14.8 12.5-14.8 22.2l0 72-64 0c-79.5 0-144 64.5-144 144 0 93.4 82.8 134.8 100.6 142.6 2.2 1 4.6 1.4 7.1 1.4l2.5 0c9.8 0 17.8-8 17.8-17.8 0-8.3-5.9-15.5-12.8-20.3-8.9-6.2-19.2-18.2-19.2-40.5 0-45 36.5-81.5 81.5-81.5l30.5 0 0 72c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l136-136c9.4-9.4 9.4-24.6 0-33.9L425.5 7c-6.9-6.9-17.2-8.9-26.2-5.2z"/></svg>
                        <span>Share</span>
                    {/if}
                </button>
            </div>
            <div class="flex items-center gap-3 mt-3">
                {#if c.institution_logo_url}
                    <img src={c.institution_logo_url} alt="{c.institution} logo" class="h-14 w-auto object-contain shrink-0 border border-gray-700 rounded bg-gray-300 p-2" />
                {/if}
                <div class="text-sm text-gray-500">
                    {#if c.institution}<p>Contributed by <span class="font-semibold text-gray-300">{c.institution}</span></p>{/if}
                    <p>{c.authors_formatted}</p>
                </div>
            </div>
        </header>

        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <h2 class="font-semibold text-gray-200 mb-3">History</h2>
            <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
            <div class="prose prose-invert prose-base max-w-none text-gray-200 text-pretty leading-relaxed">{@html c.history}</div>

            {#if c.media && c.media.length > 0}
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                    {#each c.media as media, i (i)}
                        <figure>
                            <button type="button" onclick={(e) => openLightbox(i, e)} class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg cursor-zoom-in flex items-center justify-center overflow-hidden hover:bg-gray-800 transition-colors">
                                <img src={media.url} alt={media.caption || 'Case image'} class="max-w-full max-h-full object-contain" />
                            </button>
                            {#if media.caption}
                                <figcaption class="text-sm text-gray-500 mt-1">{media.caption}</figcaption>
                            {/if}
                        </figure>
                    {/each}
                </div>
            {/if}
        </div>

        {#each questions as question (question.id)}
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
                <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                <h2 class="font-semibold text-gray-200 mb-4 text-lg prose prose-invert prose-sm max-w-none">{@html question.body}</h2>

                {#if question.user_answer}
                    <div class="bg-gray-950 rounded p-3 text-sm">
                        <p class="text-gray-400">Your answer: {question.user_answer.response_text ?? question.user_answer.selected_option_body ?? '—'}</p>
                        {#if isRevealed && question.user_answer.is_correct !== undefined}
                            <p class={question.user_answer.is_correct ? 'text-green-400 font-medium mt-1' : 'text-red-400 font-medium mt-1'}>
                                {question.user_answer.is_correct ? 'Correct!' : 'Incorrect'}
                            </p>
                        {:else if !isRevealed && revealCountdown}
                            <p class="text-gray-500 mt-1">Results in {revealCountdown}.</p>
                        {/if}
                    </div>
                {:else if isOpen && $page?.props?.auth?.user}
                    <button
                        onclick={() => activeQuestionId = question.id}
                        class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 transition-colors"
                    >
                        Answer this question
                    </button>
                {:else if isOpen && !$page?.props?.auth?.user}
                    <p class="text-sm text-gray-500"><Link href={login()} class="text-cyan-400 hover:underline">Sign in</Link> to answer.</p>
                {:else if isRevealed && $page?.props?.auth?.user}
                    <p class="text-sm text-gray-500 italic">You did not answer this question.</p>
                {:else if !isRevealed}
                    <p class="text-sm text-gray-500">
                        {#if revealCountdown}
                            Answers will be revealed in {revealCountdown}.
                        {:else}
                            Answers will be revealed soon.
                        {/if}
                    </p>
                {/if}

                {#if isRevealed && question.type === 'multiple_choice' && question.options}
                    <div class="mt-4 border-t pt-4">
                        <h3 class="font-semibold text-gray-200 mb-2">Correct Answer</h3>
                        <ul class="space-y-1">
                            {#each question.options as option (option.body)}
                                <li class="text-sm {option.is_correct ? 'text-green-400 font-medium' : 'text-gray-500'}">
                                    {option.body} {option.is_correct ? '✓' : ''}
                                </li>
                            {/each}
                        </ul>
                    </div>
                {/if}

                {#if isRevealed && question.type === 'free_text' && question.correct_answer}
                    <div class="mt-4 border-t pt-4">
                        <h3 class="font-semibold text-gray-200 mb-2">Correct Answer</h3>
                        <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                        <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html question.correct_answer}</div>
                    </div>
                {/if}
            </div>
        {/each}

        {#if isRevealed && (c.discussion || c.differential || c.references?.length || (c.additional_media && c.additional_media.length > 0))}
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-6">
                {#if c.discussion}
                    <section>
                        <h2 class="font-semibold text-gray-200 mb-3">Discussion</h2>
                        <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                        <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html c.discussion}</div>
                    </section>
                {/if}

                {#if c.differential}
                    <section>
                        <h2 class="font-semibold text-gray-200 mb-3">Differential Diagnosis</h2>
                        <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                        <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html c.differential}</div>
                    </section>
                {/if}

                {#if c.additional_media && c.additional_media.length > 0}
                    <section>
                        <h2 class="font-semibold text-gray-200 mb-3">Additional Images</h2>
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {#each c.additional_media as media, i (i)}
                                <figure>
                                    <button type="button" onclick={(e) => openAdditionalLightbox(i, e)} class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg cursor-zoom-in flex items-center justify-center overflow-hidden hover:bg-gray-800 transition-colors">
                                        <img src={media.url} alt={media.caption || 'Additional image'} class="max-w-full max-h-full object-contain" />
                                    </button>
                                    {#if media.caption}
                                        <figcaption class="text-sm text-gray-500 mt-1">{media.caption}</figcaption>
                                    {/if}
                                </figure>
                            {/each}
                        </div>
                    </section>
                {/if}

                {#if c.references?.length}
                    <section>
                        <h2 class="font-semibold text-gray-200 mb-3">References</h2>
                        <ol class="space-y-2 text-sm text-gray-300 prose prose-invert prose-sm max-w-none">
                            {#each c.references as ref, i (i)}
                                <li class="flex gap-3 items-start">
                                    <span class="shrink-0 size-6 rounded-full bg-gray-700 text-gray-400 text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                                    <span>{@html ref}</span>
                                </li>
                            {/each}
                        </ol>
                    </section>
                {/if}
            </div>
        {/if}
    </div>

<MediaLightbox
    bind:this={lightboxRef}
    images={lightboxImages}
    bind:index={lightboxIndex}
    bind:open={lightboxOpen}
    onclose={() => lightboxOpen = false}
/>

<MediaLightbox
    bind:this={additionalLightboxRef}
    images={additionalLightboxImages}
    bind:index={additionalLightboxIndex}
    bind:open={additionalLightboxOpen}
    onclose={() => additionalLightboxOpen = false}
/>

{#if activeQuestionId !== null}
    {@const activeQuestion = questions.find(q => q.id === activeQuestionId)}
    {#if activeQuestion}
        <AnswerModal
            question={activeQuestion}
            {answerUrl}
            onclose={() => activeQuestionId = null}
        />
    {/if}
{/if}
